import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  guards,
  preload,
  renderCheckbox,
  token as mintToken,
  type RecaptchaAction,
  type RecaptchaConfig,
} from '../lib/recaptcha.js';

/**
 * Attaches reCAPTCHA to one form.
 *
 * Returns what the form needs and nothing it does not: a node to render (empty
 * on v3, a checkbox on v2) and a function that produces a token at submit
 * time. A form that is not guarded gets a null node and a function that
 * resolves to undefined, so the call sites read the same either way.
 */
export function useRecaptcha(cfg: RecaptchaConfig | null, action: RecaptchaAction) {
  const ref = useRef<HTMLDivElement>(null);
  const [checkboxToken, setCheckboxToken] = useState<string | undefined>();
  const active = guards(cfg, action);

  // v3 mints on demand, so warming the script up front keeps the first submit
  // from waiting on a script load.
  useEffect(() => preload(cfg), [cfg]);

  useEffect(() => {
    if (!active || cfg?.version !== 'v2' || !ref.current) return;
    let teardown: (() => void) | null = null;
    let cancelled = false;
    void renderCheckbox(cfg, action, ref.current, setCheckboxToken).then((t) => {
      if (cancelled) t?.();
      else teardown = t;
    });
    return () => {
      cancelled = true;
      teardown?.();
      setCheckboxToken(undefined);
    };
  }, [active, cfg, action]);

  const getToken = async (): Promise<string | undefined> => {
    if (!active) return undefined;
    if (cfg?.version === 'v2') return checkboxToken;
    return mintToken(cfg, action);
  };

  const node =
    active && cfg?.version === 'v2' ? <Box ref={ref} sx={{ mt: 2, display: 'flex', justifyContent: 'center' }} /> : null;

  return { node, getToken, active };
}
